// Shared CSS for legal pages (Privacy Policy + Terms of Service).
// Inlined via a <style> tag so it applies regardless of styled-components,
// build cache, or parent layout — fixed positioning gives the page its own viewport.

export const legalCss = `
.lgl {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100vw;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: #fafaf7;
  color: #1a1d12;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', sans-serif;
  letter-spacing: -0.005em;
  z-index: 1;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.lgl, .lgl * { box-sizing: border-box; }
.lgl a { color: inherit; text-decoration: none; }
.lgl p { margin: 0 0 18px; line-height: 1.75; }
.lgl ul, .lgl ol { list-style: none; padding: 0; margin: 0; }
.lgl em { font-style: italic; color: #6b6f5d; }

/* Top bar */
.lgl-topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  background: rgba(250, 250, 247, 0.85);
  backdrop-filter: saturate(150%) blur(14px);
  -webkit-backdrop-filter: saturate(150%) blur(14px);
  border-bottom: 1px solid rgba(20, 24, 11, 0.06);
}
.lgl-topbar a {
  font-size: 14px;
  font-weight: 600;
  color: #14180b;
  letter-spacing: -0.01em;
}
.lgl-topbar nav { display: flex; gap: 28px; }
.lgl-topbar nav a {
  font-size: 13px;
  font-weight: 500;
  color: #6b6f5d;
  transition: color 0.15s ease;
}
.lgl-topbar nav a:hover { color: #6f952e; }
.lgl-topbar nav a.is-active { color: #6f952e; }

/* Hero */
.lgl-hero {
  position: relative;
  width: 100%;
  padding: 96px 24px 72px;
  background:
    radial-gradient(60% 60% at 50% 0%, rgba(111, 149, 46, 0.22) 0%, transparent 70%),
    radial-gradient(40% 40% at 80% 100%, rgba(140, 184, 58, 0.12) 0%, transparent 70%),
    linear-gradient(180deg, #ffffff 0%, #fafaf7 100%);
  border-bottom: 1px solid #ececdf;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow: hidden;
}
.lgl-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #6f952e;
  padding: 7px 16px;
  border: 1px solid rgba(111, 149, 46, 0.32);
  border-radius: 999px;
  background: rgba(111, 149, 46, 0.08);
}
.lgl-eyebrow::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #6f952e;
  box-shadow: 0 0 0 4px rgba(111, 149, 46, 0.18);
}
.lgl-title {
  font-size: clamp(44px, 7vw, 72px);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.04em;
  color: #14180b;
  margin: 24px 0 16px;
  max-width: 900px;
}
.lgl-subtitle {
  font-size: clamp(16px, 1.4vw, 19px);
  line-height: 1.55;
  color: #5a5f4d;
  max-width: 620px;
  margin: 0 0 26px;
  font-weight: 400;
}
.lgl-updated {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #4a4f3d;
  padding: 9px 18px;
  background: #ffffff;
  border: 1px solid #e4e6dc;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(20, 24, 11, 0.04);
}
.lgl-updated::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #8cb83a;
}

/* Layout */
.lgl-layout {
  max-width: 1200px;
  margin: 0 auto;
  padding: 64px 24px 140px;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 64px;
  align-items: start;
}

/* Sidebar */
.lgl-sidebar {
  position: sticky;
  top: 96px;
  align-self: start;
}
.lgl-sidebar-inner {
  padding: 24px;
  background: #ffffff;
  border: 1px solid #ececdf;
  border-radius: 20px;
  box-shadow: 0 1px 2px rgba(20, 24, 11, 0.03);
}
.lgl-sidebar-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #8a8e7a;
  margin-bottom: 14px;
}
.lgl-sidebar ol { display: flex; flex-direction: column; gap: 1px; }
.lgl-sidebar ol a {
  display: block;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 13.5px;
  color: #4a4f3d;
  line-height: 1.4;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}
.lgl-sidebar ol a:hover {
  background: rgba(111, 149, 46, 0.08);
  color: #3a4e15;
  transform: translateX(2px);
}

/* Content */
.lgl-content {
  width: 100%;
  max-width: 760px;
  font-size: 16px;
  line-height: 1.75;
  color: #2c2f23;
}
.lgl-intro {
  padding: 28px 32px;
  background: linear-gradient(180deg, #ffffff 0%, #fdfdfa 100%);
  border: 1px solid #ececdf;
  border-radius: 20px;
  margin-bottom: 56px;
  box-shadow: 0 1px 2px rgba(20, 24, 11, 0.03);
}
.lgl-intro p:last-child { margin-bottom: 0; }
.lgl-section { scroll-margin-top: 96px; margin-bottom: 64px; }
.lgl-h2 {
  font-size: clamp(26px, 2.6vw, 34px);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #14180b;
  line-height: 1.15;
  margin: 0 0 22px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ececdf;
  position: relative;
}
.lgl-h2::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 48px;
  height: 2px;
  background: linear-gradient(90deg, #6f952e 0%, #8cb83a 100%);
  border-radius: 2px;
}
.lgl-h3 {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: #14180b;
  margin: 32px 0 12px;
}
.lgl-list {
  margin: 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.lgl-list li {
  position: relative;
  padding-left: 24px;
  line-height: 1.6;
}
.lgl-list li::before {
  content: '';
  position: absolute;
  left: 4px;
  top: 11px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8cb83a 0%, #6f952e 100%);
  box-shadow: 0 0 0 3px rgba(111, 149, 46, 0.12);
}
.lgl-callout {
  margin: 0 0 24px;
  padding: 18px 22px;
  border-radius: 14px;
  border-left: 3px solid #6f952e;
  background: linear-gradient(135deg, rgba(111, 149, 46, 0.1) 0%, rgba(111, 149, 46, 0.04) 100%);
  color: #3a4e15;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.55;
}
.lgl-contact {
  padding: 28px 32px;
  background: #ffffff;
  border: 1px solid #ececdf;
  border-radius: 20px;
  line-height: 1.85;
  font-size: 15px;
  color: #2c2f23;
  box-shadow: 0 1px 2px rgba(20, 24, 11, 0.03);
}
.lgl-link {
  color: #6f952e;
  font-weight: 600;
  border-bottom: 1px solid rgba(111, 149, 46, 0.32);
  transition: color 0.15s ease, border-color 0.15s ease;
}
.lgl-link:hover {
  color: #4a6b1c;
  border-bottom-color: #4a6b1c;
}
.lgl-strong { font-weight: 600; color: #14180b; }

/* Footer */
.lgl-footer {
  border-top: 1px solid #ececdf;
  padding: 40px 24px;
  text-align: center;
  font-size: 13px;
  color: #8a8e7a;
  background: #ffffff;
}

/* Responsive */
@media (max-width: 960px) {
  .lgl-layout {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 32px 20px 80px;
  }
  .lgl-sidebar { position: relative; top: auto; }
  .lgl-topbar { padding: 14px 20px; }
  .lgl-topbar nav { gap: 18px; }
}
@media (max-width: 768px) {
  .lgl-hero { padding: 64px 20px 48px; }
  .lgl-intro, .lgl-contact { padding: 22px; }
  .lgl-h2 { font-size: 24px; }
}
`
